import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { bottomSheetDockClassName, bottomSheetFilterTitleClassName } from "@/lib/bottom-sheet-compact";
import { useIsMobile } from "@/hooks/use-mobile";
import { ContactsSectionTabs, type ContactsHubTab } from "./ContactsSectionTabs";
import { PageTitleBar } from "@/components/ui/PageTitleBar";
import { cn } from "@/lib/utils";

export function ContactsHubShell({
  title,
  activeTab,
  headerRight,
  /** Desktop: second header layer (bulk actions) when `selectionModeActive` */
  selectionToolbar,
  selectionModeActive = false,
  /** Full-width row directly under the title bar (e.g. Volanie pipeline + stage selects) */
  belowHeader,
  sidebar,
  mobileFilterFooter,
  children,
}: {
  title: string;
  activeTab: ContactsHubTab;
  headerRight?: React.ReactNode;
  selectionToolbar?: React.ReactNode;
  selectionModeActive?: boolean;
  belowHeader?: React.ReactNode;
  sidebar: React.ReactNode;
  /** Optional footer in mobile filter sheet; function receives close() */
  mobileFilterFooter?: React.ReactNode | ((close: () => void) => React.ReactNode);
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const useSelectionLayers = Boolean(selectionToolbar) && !isMobile;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background">
      <div className="relative z-20 flex h-[60px] min-h-[60px] flex-shrink-0 overflow-hidden border-b bg-background">
        {useSelectionLayers ? (
          <>
            <div
              className={cn(
                "absolute inset-0 flex items-center transition-all duration-300 ease-in-out",
                selectionModeActive
                  ? "pointer-events-none opacity-0 -translate-y-full"
                  : "translate-y-0 opacity-100",
              )}
            >
              <PageTitleBar
                className="h-full w-full min-w-0 border-0"
                rowClassName="h-full min-h-0 items-center"
                title={title}
                right={
                  <>
                    {headerRight}
                    {isMobile ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 w-9 shrink-0 p-0"
                        onClick={() => setFilterSheetOpen(true)}
                        aria-label={t("common.filterLabel")}
                      >
                        <Filter className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </>
                }
              />
            </div>
            <div
              className={cn(
                "absolute inset-0 flex items-center px-3 transition-all duration-300 ease-in-out sm:px-4",
                selectionModeActive
                  ? "translate-y-0 opacity-100"
                  : "pointer-events-none translate-y-full opacity-0",
              )}
            >
              {selectionToolbar}
            </div>
          </>
        ) : (
          <PageTitleBar
            className="h-full border-0"
            rowClassName="h-full min-h-0 items-center"
            title={title}
            right={
              <>
                {headerRight}
                {isMobile ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 w-9 shrink-0 p-0"
                    onClick={() => setFilterSheetOpen(true)}
                    aria-label={t("common.filterLabel")}
                  >
                    <Filter className="h-4 w-4" />
                  </Button>
                ) : null}
              </>
            }
          />
        )}
      </div>

      {belowHeader ? (
        <div className="z-20 flex-shrink-0 border-b bg-background px-3 py-2 sm:px-4">{belowHeader}</div>
      ) : null}

      <div className="min-h-0 flex-1 min-w-0 overflow-hidden">
        <div className="grid h-full min-h-0 grid-cols-1 gap-3 md:grid-cols-[360px,1fr] md:gap-6">
          <div className="hidden min-h-0 overflow-y-auto md:block">
            <div className="space-y-4 p-4">{sidebar}</div>
          </div>
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto" data-hub-scroll="1">
            <div className="container mx-auto w-full min-h-0 px-2 py-3 pb-[calc(96px+env(safe-area-inset-bottom))] md:px-4 md:py-4 md:pb-4">
              <ContactsSectionTabs active={activeTab} compact={!!isMobile} />
              <div className="mt-4 min-h-0">{children}</div>
            </div>
          </div>
        </div>
      </div>

      <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
        <SheetContent
          side="bottom"
          className={bottomSheetDockClassName(
            "flex max-h-[88vh] flex-col overflow-hidden",
          )}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <SheetHeader className={bottomSheetFilterTitleClassName}>
            <SheetTitle className="text-base font-semibold leading-snug">
              {t("common.filterLabel")}
            </SheetTitle>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-0 py-3">{sidebar}</div>
          {mobileFilterFooter ? (
            <div className="border-t bg-background px-4 py-3">
              {typeof mobileFilterFooter === "function"
                ? mobileFilterFooter(() => setFilterSheetOpen(false))
                : mobileFilterFooter}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
